import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import ParallaxScroll from "./ParallaxScroll";
import UserProgressContext from "../../userProgressContext";

/**
 * LevelEntryGate — cinematic gate for /level/:levelId routes.
 *
 * Renders <ParallaxScroll> first, then mounts children (GameLevelLayout +
 * GameLevel) once the intro completes or is skipped.
 *
 * When to show the intro:
 *   - localProgress is null (fresh load) OR at step x.01 (true level start)
 *   - AND the sessionStorage seen-flag is not set (placeholder levels only;
 *     real-scene levels manage their own always-show logic in ParallaxScroll)
 *
 * When to skip directly to children:
 *   - Player is mid-level (e.g. returning from /gridGuardArsenal at step 1.07)
 *   - Placeholder level already seen this session
 */
const LevelEntryGate = ({ children }) => {
  const { levelId } = useParams();
  const { localProgress } = useContext(UserProgressContext);

  // True when localProgress is at the very first step of this level (x.01).
  // null localProgress (fresh page load) also counts as level-start.
  const isAtLevelStart = (lp, id) => {
    if (lp === null) return true;
    const step = Math.round((parseFloat(lp) % 1) * 100);
    return Math.trunc(parseFloat(lp)) === parseInt(id) && step <= 1;
  };

  const seenKey = `parallax_seen_${levelId}`;
  const alreadySeen = !!sessionStorage.getItem(seenKey);

  const [parallaxDone, setParallaxDone] = useState(
    () => alreadySeen || !isAtLevelStart(localProgress, levelId)
  );

  // Re-evaluate when levelId changes (navigating between levels).
  useEffect(() => {
    const seen = !!sessionStorage.getItem(`parallax_seen_${levelId}`);
    setParallaxDone(seen || !isAtLevelStart(localProgress, levelId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId]);

  return (
    <>
      {!parallaxDone && (
        <ParallaxScroll
          levelId={levelId}
          onComplete={() => setParallaxDone(true)}
        />
      )}
      {parallaxDone && children}
    </>
  );
};

export default LevelEntryGate;
